<div class="flex flex-col gap-2 mb-4">
  <div class="pb-2">
    <div class="flex flex-col gap-1">
      <h1 class="text-lg font-bold">Feature flags</h1>
      <p class="text-sm text-gray-700">Manage tenant-level feature flags used by the platform.</p>
      <p class="text-sm text-gray-600">
        Authorize.net, Lemur, and Budco Financial Services (BFS) financing are configured under Apps > Payment
        configurations.
      </p>
    </div>
    <vs-divider class="my-4"></vs-divider>
    <form class="flex flex-col gap-4" [formGroup]="flagsForm">
      <div class="flex">
        <label class="flex items-center gap-4 cursor-pointer">
          <input
            vsInput
            type="checkbox"
            appearance="toggle"
            formControlName="declarationPage"
            [disabled]="loading || saving || (canEditFlags$ | async) === false"
          />
          <div class="text-sm">
            <p class="text-gray-700 font-medium">Declaration Page</p>
            <p class="text-gray-600">Allow customers and staff to generate the declaration page PDF for warranties.</p>
          </div>
        </label>
      </div>
      <div class="flex flex-col gap-4">
        <label class="flex items-start gap-4 cursor-pointer">
          <input
            vsInput
            type="checkbox"
            appearance="toggle"
            formControlName="dynamicRating"
            class="mt-0.5"
            [disabled]="loading || saving || dynamicRatingBackfillProcessing || (canEditFlags$ | async) === false"
          />
          <div class="flex-1 text-sm">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-gray-700 font-medium">Dynamic rating engine</p>
              <vs-badge *ngIf="dynamicRatingBackfillProcessing" theme="primary" class="theme-primary">
                Configuring
              </vs-badge>
              <vs-badge *ngIf="dynamicRatingBackfillStatus === 'completed'" theme="success" class="theme-success">
                Configured
              </vs-badge>
              <vs-badge *ngIf="dynamicRatingBackfillStatus === 'failed'" theme="danger" class="theme-danger">
                Failed
              </vs-badge>
            </div>
            <p class="text-gray-600 mt-1">Enable dynamic rating & pricing dimension checks during policy publish.</p>
          </div>
        </label>

        <div
          *ngIf="dynamicRatingBackfillProcessing"
          class="rounded-xl border border-primary-200 bg-white px-4 py-4 shadow-sm"
          role="status"
          aria-live="polite"
        >
          <div class="flex items-start gap-4">
            <div class="relative shrink-0">
              <vs-featured-icon name="layers-three-01" theme="primary"></vs-featured-icon>
              <vs-icon
                name="loading-01"
                size="14"
                class="absolute -bottom-1 -right-1 rounded-full bg-white p-0.5 text-primary-500 animate-spin"
              ></vs-icon>
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p class="text-sm font-semibold text-gray-900">Configuring Rating Matrix</p>
                  <p class="mt-1 text-sm text-gray-600">
                    Plan and term configuration is running in the background. You can stay on this page or navigate away
                    — processing will continue.
                  </p>
                </div>
                <span
                  *ngIf="dynamicRatingBackfillElapsed"
                  class="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                >
                  {{ dynamicRatingBackfillElapsed }}
                </span>
              </div>

              <div class="mt-4 h-1.5 overflow-hidden rounded-full bg-primary-100">
                <div class="backfill-progress-bar h-full w-1/2 rounded-full bg-primary-500"></div>
              </div>

              <div class="mt-4 flex flex-wrap gap-2">
                <span
                  class="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs text-primary-700"
                >
                  <vs-icon name="loading-01" size="12" class="animate-spin"></vs-icon>
                  Coverage elements
                </span>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs text-primary-700"
                >
                  <vs-icon name="loading-01" size="12" class="animate-spin"></vs-icon>
                  Term elements
                </span>
                <span
                  class="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs text-primary-700"
                >
                  <vs-icon name="loading-01" size="12" class="animate-spin"></vs-icon>
                  Seller elements
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          *ngIf="dynamicRatingBackfillShowSuccess"
          class="flex items-start gap-3 rounded-xl border border-success-200 bg-success-50 px-4 py-3"
          role="status"
        >
          <vs-featured-icon name="check-circle" theme="success"></vs-featured-icon>
          <div class="text-sm">
            <p class="font-semibold text-success-800">Rating Matrix ready</p>
            <p class="mt-1 text-success-700">
              Coverage, term, and seller elements have been configured for dynamic rating.
            </p>
          </div>
        </div>

        <div
          *ngIf="dynamicRatingBackfillStatus === 'failed' && !dynamicRatingBackfillProcessing"
          class="flex items-start gap-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3"
          role="alert"
        >
          <vs-featured-icon name="alert-triangle" theme="danger"></vs-featured-icon>
          <div class="text-sm">
            <p class="font-semibold text-danger-800">Rating Matrix configuration failed</p>
            <p class="mt-1 text-danger-700">
              {{ dynamicRatingBackfillError || 'Something went wrong while configuring plan and term elements.' }}
            </p>
            <p class="mt-2 text-danger-700">
              Refresh this page to check status, or contact support if the issue persists.
            </p>
          </div>
        </div>
      </div>
      <div class="flex" *ngIf="isFullAdmin$ | async">
        <label class="flex items-center gap-4 cursor-pointer">
          <input
            vsInput
            type="checkbox"
            appearance="toggle"
            formControlName="customRoles"
            [disabled]="loading || saving || (canEditFlags$ | async) === false"
          />
          <div class="text-sm">
            <p class="text-gray-700 font-medium">Custom roles</p>
            <p class="text-gray-600">
              Allow this tenant to edit role permissions. Enabling seeds the current roles into the tenant so they
              become configurable.
            </p>
          </div>
        </label>
      </div>
      <div class="flex">
        <label class="flex items-center gap-4 cursor-pointer">
          <input
            vsInput
            type="checkbox"
            appearance="toggle"
            formControlName="batchWarrantyPayments"
            [disabled]="loading || saving || (canEditFlags$ | async) === false"
          />
          <div class="text-sm">
            <p class="text-gray-700 font-medium">Batch warranty payments</p>
            <p class="text-gray-600">
              Show the "Unfunded Warranties" tab on sellers, letting them select provisioned warranties and pay for them
              in one charge.
            </p>
          </div>
        </label>
      </div>
      <div class="flex">
        <label class="flex items-center gap-4 cursor-pointer">
          <input
            vsInput
            type="checkbox"
            appearance="toggle"
            formControlName="claimZeroPay"
            [disabled]="loading || saving || (canEditFlags$ | async) === false"
          />
          <div class="text-sm">
            <p class="text-gray-700 font-medium">Zero-pay claim authorization</p>
            <p class="text-gray-600">
              Let adjusters authorize a claim for $0 once they confirm they reviewed it and decided to pay nothing.
            </p>
          </div>
        </label>
      </div>
      <div class="flex" *ngIf="isFullAdmin$ | async">
        <label class="flex items-center gap-4 cursor-pointer">
          <input
            vsInput
            type="checkbox"
            appearance="toggle"
            formControlName="newClaimsFlow"
            [disabled]="loading || saving || (canEditFlags$ | async) === false"
          />
          <div class="text-sm">
            <p class="text-gray-700 font-medium">{{ config.newClaimsFlow.label }}</p>
            <p class="text-gray-600">{{ config.newClaimsFlow.description }}</p>
          </div>
        </label>
      </div>
      <div class="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mt-2">
        <div class="flex justify-between w-full">
          <button
            *ngIf="flagsForm.dirty"
            vs-button
            theme="primary"
            [disabled]="loading || saving || dynamicRatingBackfillProcessing || (canEditFlags$ | async) === false"
            (click)="saveFlags()"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</div>
